import { WidgetState, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import { Fragment, useId, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronRight } from "lucide-react";
import { firstError, unwrap, Widget, HeaderStats } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { PerformanceValue } from "./PerformanceValue";
import { GROUPED_METRICS_TABLE_CONTRACT } from "./types";
import type {
  GroupedMetricsTableData,
  MetricColumn,
  MetricColumnGroup,
  MetricTableRow,
} from "./types";

export interface GroupedMetricsTableConfig {
  title?: string;
}

export const manifest: WidgetManifest = {
  id: "grouped-metrics-table",
  name: "Grouped metrics table",
  description:
    "Sortable table comparing entities across labeled metric groups, with an optional per-row drill-down.",
  version: "1.0.0",
  dataRequests: {
    table: {
      source: "grouped-metrics-table",
      contract: GROUPED_METRICS_TABLE_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: GroupedMetricsTableConfig = {};

const countFormat = new Intl.NumberFormat("en");

function formatValue(value: number | null | undefined, kind: MetricColumn["kind"]) {
  if (value === null || value === undefined) return <span className="text-muted">–</span>;
  if (kind === "percent") return <PerformanceValue fraction={value} />;
  return <span className="tabular-nums text-secondary">{countFormat.format(value)}</span>;
}

function flatColumns(groups: MetricColumnGroup[]): MetricColumn[] {
  return groups.flatMap((group) => group.columns);
}

function MobileRow({ row, columns }: { row: MetricTableRow; columns: MetricColumn[] }) {
  const [open, setOpen] = useState(false);
  return (
    <article className="border-b border-border py-3 last:border-0">
      <button
        type="button"
        onClick={() => row.drilldown?.length && setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-2 text-left"
        aria-expanded={row.drilldown?.length ? open : undefined}
      >
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-primary">
          {row.drilldown?.length ? (
            <ChevronRight
              className={`h-3.5 w-3.5 shrink-0 text-muted transition-transform ${open ? "rotate-90" : ""}`}
              aria-hidden="true"
            />
          ) : null}
          {row.label}
        </h3>
      </button>
      <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 text-xs @sm/widget:grid-cols-2 @3xl/widget:grid-cols-3">
        {columns.map((column) => (
          <div key={column.key} className="flex items-center justify-between gap-2">
            <dt className="text-muted">{column.label}</dt>
            <dd className="font-semibold">{formatValue(row.values[column.key], column.kind)}</dd>
          </div>
        ))}
      </dl>
      {open &&
        row.drilldown?.map((group) => (
          <div key={group.label} className="mt-3 border-t border-border pt-2">
            <p className="text-2xs font-semibold uppercase tracking-wider text-muted">
              {group.label}
            </p>
            <ul className="mt-1 flex flex-col gap-1.5">
              {group.rows.map((sub) => (
                <li key={sub.id} className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-secondary">{sub.label}</span>
                  <span className="flex gap-3">
                    {group.columnKeys.map((key) => {
                      const column = columns.find((c) => c.key === key);
                      if (!column) return null;
                      return <span key={key}>{formatValue(sub.values[key], column.kind)}</span>;
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </article>
  );
}

function DesktopRow({
  row,
  columns,
  groupStartKeys,
}: {
  row: MetricTableRow;
  columns: MetricColumn[];
  groupStartKeys: Set<string>;
}) {
  const [open, setOpen] = useState(false);
  const contentId = useId();
  const hasDrilldown = Boolean(row.drilldown?.length);

  return (
    <>
      <tr
        className={`border-b border-border transition-colors ${open ? "bg-surface-elevated" : "hover:bg-surface-elevated"}`}
      >
        <td className="py-2 pl-4 pr-4 text-primary">
          {hasDrilldown ? (
            <button
              type="button"
              aria-expanded={open}
              aria-controls={contentId}
              onClick={() => setOpen((value) => !value)}
              className="flex w-full items-center gap-1.5 rounded text-left font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              <ChevronRight
                className={`h-3.5 w-3.5 shrink-0 text-muted transition-transform ${open ? "rotate-90" : ""}`}
                aria-hidden="true"
              />
              {row.label}
            </button>
          ) : (
            <span className="pl-5 font-medium">{row.label}</span>
          )}
        </td>
        {columns.map((column) => (
          <td
            key={column.key}
            className={`px-3 py-2 text-center ${groupStartKeys.has(column.key) ? "border-l border-border" : ""}`}
          >
            {formatValue(row.values[column.key], column.kind)}
          </td>
        ))}
      </tr>
      {open &&
        row.drilldown?.map((group) => (
          <Fragment key={group.label}>
            <tr className="border-y border-border bg-surface-elevated">
              <td className="py-1.5 pl-8 pr-4 text-2xs font-semibold uppercase tracking-wider text-muted">
                {group.label}
              </td>
              <td colSpan={columns.length} />
            </tr>
            {group.rows.map((sub) => (
              <tr key={sub.id} className="bg-surface text-xs">
                <td className="py-1 pl-8 pr-4 text-secondary">{sub.label}</td>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`px-3 py-1 text-center ${groupStartKeys.has(column.key) ? "border-l border-border" : ""}`}
                  >
                    {group.columnKeys.includes(column.key)
                      ? formatValue(sub.values[column.key], column.kind)
                      : null}
                  </td>
                ))}
              </tr>
            ))}
          </Fragment>
        ))}
    </>
  );
}

export function Component({ data, config }: WidgetProps<GroupedMetricsTableConfig>) {
  const error = firstError(data, ["table"]);
  const result = unwrap<GroupedMetricsTableData>(data.table);
  const loading = data.table?.isLoading && !result;
  const heading = config.title ?? manifest.name;

  const columns = useMemo(() => flatColumns(result?.columnGroups ?? []), [result]);
  const groupStartKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const group of result?.columnGroups ?? []) {
      if (group.columns[0]) keys.add(group.columns[0].key);
    }
    return keys;
  }, [result]);

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  function handleSort(key: string) {
    if (key === sortKey) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  const sortedRows = useMemo(() => {
    const rows = result?.rows ?? [];
    if (!sortKey) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a.values[sortKey] ?? -Infinity;
      const bv = b.values[sortKey] ?? -Infinity;
      return (av - bv) * dir;
    });
  }, [result, sortKey, sortDir]);

  return (
    <Widget
      title={heading}
      headerSummary={
        result?.summary?.length ? (
          <HeaderStats items={result.summary} label={`${heading} summary`} />
        ) : undefined
      }
    >
      {error ? (
        <WidgetIssueState error={error} />
      ) : loading || !result ? (
        <WidgetSkeleton shape="table" />
      ) : result.rows.length === 0 ? (
        <WidgetState title="No rows for this period." />
      ) : (
        <div className="p-2">
          <div className="divide-y-0 @3xl/widget:hidden">
            {sortedRows.map((row) => (
              <MobileRow key={row.id} row={row} columns={columns} />
            ))}
          </div>
          <div className="hidden overflow-x-auto rounded-md border border-border @3xl/widget:block">
            <table className="w-full table-fixed text-sm">
              <thead className="bg-surface-elevated">
                <tr className="text-2xs uppercase tracking-wider text-muted">
                  <th className="py-1 pr-4" />
                  {result.columnGroups.map((group) => (
                    <th
                      key={group.id}
                      className="border-b border-l border-border px-3 py-2 text-center font-semibold"
                      colSpan={group.columns.length}
                      scope="colgroup"
                      style={group.accentColor ? { color: group.accentColor } : undefined}
                    >
                      {group.label}
                    </th>
                  ))}
                </tr>
                <tr className="border-b border-border text-2xs uppercase tracking-wider text-muted">
                  <th className="py-2 pl-4 pr-4 text-left" />
                  {columns.map((column) => {
                    const active = sortKey === column.key;
                    return (
                      <th
                        key={column.key}
                        scope="col"
                        aria-sort={
                          active ? (sortDir === "asc" ? "ascending" : "descending") : "none"
                        }
                        className={`px-3 py-2 text-center ${groupStartKeys.has(column.key) ? "border-l border-border" : ""}`}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(column.key)}
                          aria-label={`Sort by ${column.label}`}
                          className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wider transition-colors hover:text-secondary ${active ? "text-primary" : ""}`}
                        >
                          {column.label}
                          {active ? (
                            sortDir === "asc" ? (
                              <ArrowUp className="h-3 w-3" />
                            ) : (
                              <ArrowDown className="h-3 w-3" />
                            )
                          ) : null}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <DesktopRow
                    key={row.id}
                    row={row}
                    columns={columns}
                    groupStartKeys={groupStartKeys}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<GroupedMetricsTableConfig> = {
  manifest,
  Component,
  defaultConfig,
};
export default widgetModule;
